// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// "Upcoming appointment" context surfaced at the top of the check-in. Every field is
// nullable — we render only what the backend supplied, and nothing at all when the visit
// block is absent or empty.

import { useTranslation } from 'react-i18next'
import { CalendarClock, MapPin } from 'lucide-react'
import { useLanguage } from '@/i18n/use-language'
import type { IntakeVisit } from '@/intake/types'
import { formatDisplayDateTime } from '@/lib/date-format'

function formatAppointment(iso: string | null | undefined, locale: string): string | null {
  const formatted = formatDisplayDateTime(iso, { locale, fallback: '' })
  return formatted || null
}

/** Provider display name, or the localized "your provider" fallback. */
export function providerLabel(visit: IntakeVisit | null, fallback: string): string {
  const name = visit?.providerName?.trim()
  return name || fallback
}

export function AppointmentCard({ visit }: { visit: IntakeVisit | null }) {
  const { t } = useTranslation()
  const { current } = useLanguage()
  if (!visit) return null

  const when = formatAppointment(visit.appointmentDateTime, current.code)
  const provider = visit.providerName?.trim()
  const location = visit.location?.trim()
  // Nothing worth showing → render nothing.
  if (!when && !provider && !location) return null

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-primary/20 bg-gradient-to-br from-[#006EF9]/8 to-[#02C7C3]/8 px-4 py-3.5 my-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <CalendarClock className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {t('chat.upcomingAppointment')}
          </p>
          {when && <p className="mt-0.5 text-[14px] font-semibold leading-snug text-foreground">{when}</p>}
          {provider && (
            <p className="text-[13px] text-muted-foreground">{t('chat.appointmentWith', { provider })}</p>
          )}
          {location && (
            <p className="mt-1 flex items-center gap-1 text-[12px] text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{location}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
